import StudentDraftAnswer from "./StudentDraftAnswer";
import { useUnsavedAnswer } from "../hooks/useUnsavedAnswer";
import TypedAnswerEditor from "./TypedAnswerEditor";
import { studentInputSettings } from "../utils/answerDocument";
import React, { useEffect, useRef, useState } from "react";
import { useApiClient } from "../../../api";
import { EditExtractedTextDialog } from "./EditExtractedTextDialog";
import { Button, FormControl, Stack, Typography } from "@mui/material";
import { Slideshow } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { PastDeadlineChip } from "../states/PastDeadlineChip";
import { hasAnswerText } from "../utils/answerSubmission";

const TeacherAnswerEditor = ({
  answer,
  assignmentQuestion,
  student,
  onSuccessfulSubmit,
  onCancelEditing,
  editingExisting = false,
  disableSubmit = false,
  studentView = false,
  onDirtyChange,
}) => {
  const [answerDocument, setAnswerDocument] = useState(
    answer?.answerDocument || null,
  );
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const settings = studentInputSettings(
    assignmentQuestion?.studentInputSettings,
  );
  const [answerText, setAnswerText] = useState("");
  const [extractedTextDialogOpen, setExtractedTextDialogOpen] = useState(false);
  const ApiClient = useApiClient();
  const dirty =
    answerText !== (answer?.answer || "") ||
    (answerDocument !== null &&
      JSON.stringify(answerDocument) !==
        JSON.stringify(answer?.answerDocument || null));
  useUnsavedAnswer(dirty);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    setAnswerText(answer?.answer || "");
    setAnswerDocument(answer?.answerDocument || null);
  }, [answer?.id, answer?.answer, answer?.answerDocument]);

  const handleSubmit = async () => {
    if (!hasAnswerText(answerText) || submitting.current || disableSubmit)
      return;
    submitting.current = true;
    setSaving(true);

    try {
      const response = await ApiClient.assignment.submitAnswer({
        assignmentQuestion:
          assignmentQuestion?.id || answer?.assignmentQuestion?.id,
        student: student?.id || answer?.student?.id,
        answer: answerText,
        ...(answerDocument ? { answerDocument } : {}),
      });
      if (response.success) {
        enqueueSnackbar("Answer submitted!", {
          variant: "success",
          autoHideDuration: 3000,
        });
        onDirtyChange?.(false);
        onSuccessfulSubmit?.();
        // navigate(`/assignments/view/${answer.assignment.id}`);
      } else {
        enqueueSnackbar(response.message || "Error submitting!", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to submit answer", error);
      enqueueSnackbar("Error submitting!", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <EditExtractedTextDialog
        open={extractedTextDialogOpen}
        setOpen={setExtractedTextDialogOpen}
        answer={answerText}
        setAnswer={setAnswerText}
        chosenAnswer={answer}
        onRetrySuccess={async () => {
          setExtractedTextDialogOpen(false);
          await onSuccessfulSubmit?.();
        }}
        disableRetry={disableSubmit}
      />
      <Stack
        direction={"row"}
        alignItems={"flex-end"}
        justifyContent={"space-between"}
        sx={{ mt: 2 }}
      >
        {answer?.handwrittenAnswerFile && answer && !disableSubmit ? (
          <Stack direction={"row"} spacing={2} alignItems={"center"}>
            <Typography>
              This answer has associated handwritten images
            </Typography>
            <Button
              variant={"outlined"}
              onClick={() => setExtractedTextDialogOpen(true)}
              endIcon={<Slideshow />}
            >
              Edit in Viewer
            </Button>
          </Stack>
        ) : (
          <span></span>
        )}
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          {disableSubmit ? (
            <PastDeadlineChip />
          ) : (
            <>
              {editingExisting && (
                <Button variant={"outlined"} onClick={onCancelEditing}>
                  Cancel
                </Button>
              )}
              <Button
                variant={"contained"}
                onClick={handleSubmit}
                disabled={saving || !hasAnswerText(answerText)}
              >
                Submit
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <FormControl fullWidth>
        {!hasAnswerText(answerText) && (
          <Typography color="error">Answer text is required</Typography>
        )}
        <TypedAnswerEditor
          text={answerText}
          document={answerDocument}
          onChange={(text, document) => {
            setAnswerText(text);
            setAnswerDocument(document);
          }}
          disabled={disableSubmit || saving}
          showPermissionStatus={studentView}
          allowSpelling={
            !studentView || settings.allow_spelling_assistance !== false
          }
          allowClipboard={!studentView || settings.allow_copy_paste !== false}
        />
      </FormControl>
    </React.Fragment>
  );
};

const AnswerEditor = (props) => {
  if (props.studentView) {
    const question =
      props.assignmentQuestion || props.answer?.assignmentQuestion;
    const studentId = props.student?.id || props.answer?.student?.id;
    return (
      <StudentDraftAnswer
        key={`${question?.id}-${studentId}`}
        assignmentQuestion={question?.id}
        student={studentId}
        settings={studentInputSettings(question?.studentInputSettings)}
        disabled={props.disableSubmit}
        onDirtyChange={props.onDirtyChange}
        onSuccessfulSubmit={props.onSuccessfulSubmit}
        onCancelEditing={
          props.editingExisting ? props.onCancelEditing : undefined
        }
      />
    );
  }
  return <TeacherAnswerEditor {...props} />;
};

export { AnswerEditor };
